import {debounce, once} from './core.js';

function bindQuantity(input) {
	if (!once(input, 'quantity')) {
		return;
	}
	input.type = 'number';
	input.min = input.min || '0';
	input.step = input.step || '1';
	input.inputMode = 'numeric';

	const control = input.closest('[data-quantity-control]');
	if (!control || !once(control, 'quantity-control')) {
		return;
	}
	const decrease = control.querySelector('[data-quantity-decrease]');
	const increase = control.querySelector('[data-quantity-increase]');
	const submit = debounce(() => {
		if (control.hasAttribute('data-quantity-submit') && input.value !== '') {
			input.form?.requestSubmit();
		}
	});
	const getNumber = (value, fallback) => {
		if (value === '' || value === null || value === undefined) {
			return fallback;
		}
		const number = Number(value);
		return Number.isFinite(number) ? number : fallback;
	};
	const sync = () => {
		const value = getNumber(input.value, getNumber(input.min, 0));
		const min = getNumber(input.min, Number.NEGATIVE_INFINITY);
		const max = getNumber(input.max, Number.POSITIVE_INFINITY);
		if (decrease) {
			decrease.disabled = value <= min;
		}
		if (increase) {
			increase.disabled = value >= max;
		}
	};
	const change = (direction) => {
		const step = getNumber(input.step, 1);
		const min = getNumber(input.min, Number.NEGATIVE_INFINITY);
		const max = getNumber(input.max, Number.POSITIVE_INFINITY);
		const value = getNumber(input.value, Number.isFinite(min) ? min : 0);
		input.value = String(Math.min(max, Math.max(min, value + direction * step)));
		input.dispatchEvent(new Event('input', {bubbles: true}));
		input.dispatchEvent(new Event('change', {bubbles: true}));
	};
	decrease?.addEventListener('click', () => change(-1));
	increase?.addEventListener('click', () => change(1));
	input.addEventListener('input', sync);
	input.addEventListener('change', submit);
	sync();
}

function bindMaxLength(input) {
	if (!input.maxLength || input.maxLength < 1 || !once(input, 'maxlength')) {
		return;
	}
	const output = document.createElement('small');
	output.className = 'form-text text-muted maxlength-counter';
	output.setAttribute('aria-live', 'polite');
	const update = () => output.textContent = `${input.value.length}/${input.maxLength}`;
	input.insertAdjacentElement('afterend', output);
	input.addEventListener('input', update);
	update();
}

function confirmDialog(trigger) {
	const dialog = document.getElementById('frontend-confirm');
	if (!dialog || !window.jQuery) {
		return;
	}
	dialog.querySelector('.modal-title').textContent = trigger.dataset.confirmTitle || '';
	dialog.querySelector('.modal-body p').textContent = trigger.dataset.confirmText || '';
	const accept = dialog.querySelector('[data-confirm-accept]');
	accept.classList.remove('btn-primary', 'btn-danger', 'btn-warning');
	accept.classList.add(['btn-danger', 'btn-warning'].includes(trigger.dataset.confirmOkClass) ? trigger.dataset.confirmOkClass : 'btn-primary');
	accept.onclick = () => {
		accept.disabled = true;
		accept.setAttribute('aria-busy', 'true');
		if (trigger.dataset.ajax === 'on') {
			window.naja.makeRequest('GET', trigger.href, null, {history: false})
				.catch(console.error).finally(() => window.jQuery(dialog).modal('hide'));
		} else if (trigger.matches('input, button')) {
			trigger.closest('form')?.submit();
		} else {
			window.location.assign(trigger.href);
		}
	};
	window.jQuery(dialog).one('hidden.bs.modal', () => {
		accept.disabled = false;
		accept.removeAttribute('aria-busy');
		trigger.focus();
	}).modal('show');
}

export function init(root = document) {
	root.querySelectorAll('.input-number, [data-quantity]').forEach(bindQuantity);
	root.querySelectorAll('input[maxlength], textarea[maxlength]').forEach(bindMaxLength);
	root.querySelectorAll('[data-confirm]').forEach((trigger) => {
		if (!once(trigger, 'confirm')) {
			return;
		}
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
			confirmDialog(trigger);
		}, true);
	});
	root.querySelectorAll('[data-tab-target]').forEach((trigger) => {
		if (once(trigger, 'tab-target')) {
			trigger.addEventListener('click', () => window.jQuery?.(`a[href="${trigger.dataset.tabTarget}"]`).tab('show'));
		}
	});
	root.querySelectorAll('[data-collapse-icon]').forEach((icon) => {
		if (!once(icon, 'collapse-icon')) {
			return;
		}
		const target = document.querySelector(icon.dataset.collapseIcon);
		if (target && window.jQuery) {
			window.jQuery(target).on('show.bs.collapse', () => icon.classList.replace('bi-caret-right-fill', 'bi-caret-down-fill'));
			window.jQuery(target).on('hide.bs.collapse', () => icon.classList.replace('bi-caret-down-fill', 'bi-caret-right-fill'));
		}
	});
	root.querySelectorAll('[data-persist-session]').forEach((input) => {
		if (!once(input, 'persist-session')) {
			return;
		}
		const key = `form-value:${input.dataset.persistSession}`;
		if (!input.value) {
			input.value = sessionStorage.getItem(key) || '';
		}
		input.addEventListener('input', () => sessionStorage.setItem(key, input.value));
	});
	root.querySelectorAll('form').forEach((form) => {
		if (!once(form, 'busy-submit')) {
			return;
		}
		form.addEventListener('submit', () => {
			form.querySelectorAll('[data-order-submit], [data-busy-on-submit]').forEach((button) => {
				button.disabled = true;
				button.setAttribute('aria-busy', 'true');
			});
		});
	});
}
